

import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Chip, CircularProgress, Alert,
  TextField, InputAdornment, Button, Card, CardContent,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Tabs, Tab, Select, MenuItem,
  FormControl, InputLabel, Switch, FormControlLabel,
  Divider, Grid, Snackbar,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import recruiterTheme, {
  FONT_BODY, FONT_DISPLAY, TYPE, RADIUS, SHADOW, PAGE_PADDING, COLORS, CONTROL_HEIGHT, ELLIPSIS, humanizeStatus,
} from "../../../styles/recruiterTheme";
import {
  Search, ArrowBack, CheckCircle, HourglassEmpty, Refresh,
  Visibility, Download, Close, Person, Assignment,
  Security, AccountBalance, Description,
  RecordVoiceOver, VerifiedUser, Done, Upload,
} from "@mui/icons-material";

// Theme tokens (the old slate/indigo values are mapped onto the app palette).
const NAVY    = COLORS.primaryDeep;
const INDIGO  = COLORS.primary;
const SLATE   = COLORS.muted;
const LINE    = COLORS.border;
const API_BASE = process.env.REACT_APP_API_BASE_URL;

// utils/apiInterceptor.js adds the Bearer token to every fetch.
function getAuthHeaders() {
  return {};
}

const JSON_HEADERS = { "Content-Type": "application/json" };

// fetch + JSON parse; throws on HTTP errors and on { success: false }.
async function api(url, options = {}) {
  const res = await fetch(url, options);
  let data = {};
  try { data = await res.json(); } catch { /* empty / non-JSON body */ }
  if (!res.ok || data?.success === false) {
    throw new Error(data?.message || data?.detail || `Request failed (${res.status})`);
  }
  return data;
}

const fmtDate = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtDateTime = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${fmtDate(raw)} · ${hh}:${mm}`;
};

// One in-flight request per form: busy flag, error message, success callback.
function useAction(onDone) {
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const run = async (fn, successMsg) => {
    if (busy) return;
    setBusy(true); setError("");
    try {
      await fn();
      onDone?.(successMsg);
    } catch (e) {
      setError(e?.message || "Request failed");
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

function ErrorAlert({ error, onClose }) {
  if (!error) return null;
  return <Alert severity="error" onClose={onClose} sx={{ mb: 2, borderRadius: `${RADIUS.sm}px` }}>{error}</Alert>;
}

// Shared button looks.
const btnBase = { height: CONTROL_HEIGHT.md, textTransform: "none", fontWeight: 600, borderRadius: `${RADIUS.sm}px` };
const primaryBtn = { ...btnBase, bgcolor: INDIGO, "&:hover": { bgcolor: COLORS.primaryDark } };
const approveBtn = { ...btnBase, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } };
const rejectBtn  = { ...btnBase, borderColor: "#ef4444", color: "#ef4444", "&:hover": { bgcolor: "#fee2e2", borderColor: "#ef4444" } };

// ── Helpers ───────────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  "Initiated":                 { bg: "#dbeafe", color: "#1e40af" },
  "Manager Approval Pending":  { bg: "#fef9c3", color: "#854d0e" },
  "HR Approval Pending":       { bg: "#ede9fe", color: "#5b21b6" },
  "Asset Collection Pending":  { bg: "#ffedd5", color: "#9a3412" },
  "Finance Clearance Pending": { bg: "#fce7f3", color: "#9d174d" },
  "Completed":                 { bg: "#dcfce7", color: "#166534" },
  "Cancelled":                 { bg: "#f1f5f9", color: "#64748b" },
  "Not Initiated":             { bg: "#f1f5f9", color: "#64748b" },
  "Approved":                  { bg: "#dcfce7", color: "#166534" },
  "Rejected":                  { bg: "#fee2e2", color: "#991b1b" },
  "Pending":                   { bg: "#fef9c3", color: "#854d0e" },
  "Returned":                  { bg: "#dcfce7", color: "#166534" },
  "Revoked":                   { bg: "#dcfce7", color: "#166534" },
  "Missing":                   { bg: "#fee2e2", color: "#991b1b" },
  "Damaged":                   { bg: "#ffedd5", color: "#9a3412" },
};

function StatusChip({ label, size = "small" }) {
  if (!label) return null;
  const s = STATUS_COLORS[label] || { bg: "#f1f5f9", color: "#64748b" };
  return (
    <Chip label={humanizeStatus(label)} size={size}
      sx={{ fontWeight: 700, height: 22, borderRadius: `${RADIUS.pill}px`, bgcolor: s.bg, color: s.color, whiteSpace: "nowrap" }} />
  );
}

function SectionCard({ title, icon, children, action }) {
  return (
    <Card elevation={0} sx={{ border: `1px solid ${LINE}`, borderRadius: `${RADIUS.lg}px`, boxShadow: SHADOW.card, mb: 2 }}>
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            {icon}
            <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle, color: NAVY }}>{title}</Typography>
          </Box>
          {action}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box display="flex" gap={1.5} py={0.5}>
      <Typography sx={{ fontSize: TYPE.small, color: SLATE, minWidth: 150, flexShrink: 0 }}>{label}</Typography>
      <Typography title={typeof value === "string" ? value : undefined} sx={{ fontSize: TYPE.small, color: NAVY, fontWeight: 600, ...ELLIPSIS }}>{value || "—"}</Typography>
    </Box>
  );
}

// ── Document preview dialog ───────────────────────────────────────────────────
function DocPreviewDialog({ open, onClose, url, name, fileName }) {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setCT]  = useState("");

  useEffect(() => {
    if (!open || !url) return;
    let cur = null;
    setLoading(true); setError(""); setBlobUrl(null);
    let cancelled = false;
    fetch(url, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`Server ${res.status}`);
        const ct = res.headers.get("content-type") || "application/octet-stream";
        return res.arrayBuffer().then(buf => ({ buf, ct }));
      })
      .then(({ buf, ct }) => {
        if (cancelled) return;
        const blob = new Blob([buf], { type: ct });
        cur = URL.createObjectURL(blob);
        setBlobUrl(cur); setCT(ct); setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(e.message || "Could not load file"); setLoading(false); } });
    return () => { cancelled = true; if (cur) URL.revokeObjectURL(cur); };
  }, [open, url]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl; a.download = fileName || name;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const isImage = contentType.startsWith("image/");
  const isPdf   = contentType === "application/pdf";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px`, overflow: "hidden" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
        bgcolor: COLORS.surfaceTint, borderBottom: `1px solid ${LINE}`, py: 1.5 }}>
        <Box minWidth={0}>
          <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, color: NAVY, ...ELLIPSIS }}>{name}</Typography>
          <Typography title={fileName} sx={{ fontSize: TYPE.caption, color: SLATE, ...ELLIPSIS }}>{fileName}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close"><Close sx={{ fontSize: 20 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: "#0f172a08", minHeight: 400,
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        {loading && <CircularProgress sx={{ color: INDIGO }} />}
        {!loading && error && <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>}
        {!loading && !error && blobUrl && isImage && (
          <Box sx={{ width: "100%", textAlign: "center", p: 2 }}>
            <img src={blobUrl} alt={name}
              style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: "8px" }} />
          </Box>
        )}
        {!loading && !error && blobUrl && isPdf && (
          <iframe src={blobUrl} title={name} style={{ width: "100%", height: "75vh", border: "none" }} />
        )}
        {!loading && !error && blobUrl && !isImage && !isPdf && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: TYPE.body, color: SLATE, mb: 2 }}>
              This file type cannot be previewed. Please download it.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${LINE}`, bgcolor: COLORS.surfaceTint }}>
        <Button onClick={onClose} sx={{ ...btnBase, color: SLATE }}>Close</Button>
        <Button variant="contained" startIcon={<Download sx={{ fontSize: 16 }} />}
          onClick={handleDownload} disabled={!blobUrl}
          sx={primaryBtn}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  TAB PANELS  (all API calls use empId — the human-readable ID e.g. "ZTL-301")
// ══════════════════════════════════════════════════════════════════════════════

// ── Overview ──────────────────────────────────────────────────────────────────
function OverviewTab({ ob, employee, empId, onRefresh }) {
  const { busy: saving, error, setError, run } = useAction(onRefresh);
  const [form, setForm]     = useState({
    exit_type:          ob.exit_type || "Resignation",
    resignation_date:   ob.resignation_date ? ob.resignation_date.slice(0, 10) : "",
    last_working_day:   ob.last_working_day ? ob.last_working_day.slice(0, 10) : "",
    notice_period_days: ob.notice_period_days || 0,
    reason:             ob.reason || "",
    remarks:            ob.remarks || "",
  });

  const handleSave = () => run(() => api(`${API_BASE}/offboarding/${empId}`, {
    method: "PUT", headers: JSON_HEADERS, body: JSON.stringify(form),
  }), "Exit details saved");

  return (
    <Box>
      <ErrorAlert error={error} onClose={() => setError("")} />
      <SectionCard title="Employee Information" icon={<Person sx={{ fontSize: 18, color: INDIGO }} />}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <InfoRow label="Employee ID"    value={employee?.emp_id} />
            <InfoRow label="Name"           value={employee?.name} />
            <InfoRow label="Email"          value={employee?.email} />
            <InfoRow label="Designation"    value={employee?.designation} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InfoRow label="Department"        value={employee?.department} />
            <InfoRow label="Reporting Manager" value={employee?.reporting_manager} />
            <InfoRow label="Current Client"    value={employee?.current_client} />
            <InfoRow label="Date of Joining"   value={fmtDate(employee?.date_of_joining)} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Exit Details" icon={<Assignment sx={{ fontSize: 18, color: INDIGO }} />}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Exit Type</InputLabel>
              <Select value={form.exit_type} label="Exit Type"
                onChange={e => setForm(f => ({ ...f, exit_type: e.target.value }))}>
                {["Resignation", "Termination", "Contract End", "Retirement"].map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="number"
              label="Notice Period (days)"
              value={form.notice_period_days}
              onChange={e => setForm(f => ({ ...f, notice_period_days: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="date"
              label="Resignation Date" InputLabelProps={{ shrink: true }}
              value={form.resignation_date}
              onChange={e => setForm(f => ({ ...f, resignation_date: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="date"
              label="Last Working Day" InputLabelProps={{ shrink: true }}
              value={form.last_working_day}
              onChange={e => setForm(f => ({ ...f, last_working_day: e.target.value }))} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline rows={2}
              label="Reason for Exit" value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline rows={2}
              label="Remarks" value={form.remarks}
              onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
          </Grid>
        </Grid>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={primaryBtn}>
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </Box>
      </SectionCard>
    </Box>
  );
}

// ── Manager Approval ──────────────────────────────────────────────────────────
function ManagerApprovalTab({ ob, empId, onRefresh }) {
  const ma = ob.manager_approval || {};
  const { busy: saving, error, setError, run } = useAction(onRefresh);
  const [form, setForm]     = useState({
    approved_by:            ma.approved_by || "",
    remarks:                ma.remarks || "",
    replacement_required:   ma.replacement_required || false,
    replacement_request_id: ma.replacement_request_id || "",
    kt_status:              ma.kt_status || "Not Started",
  });

  const handleAction = (action) => run(() => api(`${API_BASE}/offboarding/${empId}/manager-approval`, {
    method: "PUT", headers: JSON_HEADERS, body: JSON.stringify({ action, ...form }),
  }), action === "approve" ? "Manager approval recorded" : "Manager rejection recorded");

  const alreadyActed = ma.status === "Approved" || ma.status === "Rejected";

  return (
    <SectionCard title="Manager Approval" icon={<VerifiedUser sx={{ fontSize: 18, color: INDIGO }} />}>
      <ErrorAlert error={error} onClose={() => setError("")} />
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography sx={{ fontSize: TYPE.small, color: SLATE }}>Current status</Typography>
        <StatusChip label={ma.status || "Pending"} />
      </Box>
      {ma.approved_at && <InfoRow label="Actioned at" value={fmtDateTime(ma.approved_at)} />}
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Approved By"
            value={form.approved_by} disabled={alreadyActed}
            onChange={e => setForm(f => ({ ...f, approved_by: e.target.value }))} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" disabled={alreadyActed}>
            <InputLabel>KT Status</InputLabel>
            <Select value={form.kt_status} label="KT Status"
              onChange={e => setForm(f => ({ ...f, kt_status: e.target.value }))}>
              {["Not Started", "In Progress", "Completed"].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" multiline rows={2} label="Remarks"
            value={form.remarks} disabled={alreadyActed}
            onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch checked={form.replacement_required} disabled={alreadyActed}
                onChange={e => setForm(f => ({ ...f, replacement_required: e.target.checked }))} />
            }
            label="Replacement Required"
          />
        </Grid>
        {form.replacement_required && (
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Replacement Request ID"
              value={form.replacement_request_id} disabled={alreadyActed}
              onChange={e => setForm(f => ({ ...f, replacement_request_id: e.target.value }))} />
          </Grid>
        )}
      </Grid>
      {!alreadyActed && (
        <Box display="flex" gap={1.5} mt={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => handleAction("reject")} disabled={saving}
            sx={rejectBtn}>
            Reject
          </Button>
          <Button variant="contained" onClick={() => handleAction("approve")} disabled={saving}
            sx={approveBtn}>
            {saving ? "Saving…" : "Approve"}
          </Button>
        </Box>
      )}
    </SectionCard>
  );
}

// ── KT ────────────────────────────────────────────────────────────────────────
function KTTab({ ob, empId, onRefresh }) {
  const kt = ob.knowledge_transfer || {};
  const { busy: saving, error, setError, run } = useAction(onRefresh);
  const upload = useAction(onRefresh);
  const [form, setForm]         = useState({
    assigned_to:    kt.assigned_to || "",
    start_date:     kt.start_date ? kt.start_date.slice(0, 10) : "",
    end_date:       kt.end_date   ? kt.end_date.slice(0, 10)   : "",
    status:         kt.status     || "Not Started",
    handover_notes: kt.handover_notes || "",
  });

  const uploading = upload.busy;

  const handleSave = () => run(() => api(`${API_BASE}/offboarding/${empId}/kt`, {
    method: "PUT", headers: JSON_HEADERS, body: JSON.stringify(form),
  }), "KT details saved");

  const handleUploadKT = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    upload.run(() => api(`${API_BASE}/offboarding/${empId}/kt/upload`, {
      method: "POST", headers: getAuthHeaders(), body: fd,
    }), "KT document uploaded");
  };

  return (
    <SectionCard title="Knowledge Transfer" icon={<RecordVoiceOver sx={{ fontSize: 18, color: INDIGO }} />}>
      <ErrorAlert error={error || upload.error} onClose={() => { setError(""); upload.setError(""); }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Assigned To"
            value={form.assigned_to}
            onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>KT Status</InputLabel>
            <Select value={form.status} label="KT Status"
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              {["Not Started", "In Progress", "Completed"].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" type="date" label="Start Date"
            InputLabelProps={{ shrink: true }} value={form.start_date}
            onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" type="date" label="End Date"
            InputLabelProps={{ shrink: true }} value={form.end_date}
            onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" multiline rows={3} label="Handover Notes"
            value={form.handover_notes}
            onChange={e => setForm(f => ({ ...f, handover_notes: e.target.value }))} />
        </Grid>
      </Grid>

      <Box mt={2} p={2} sx={{ border: `1px dashed ${COLORS.borderStrong}`, borderRadius: `${RADIUS.sm}px` }}>
        <Typography sx={{ fontSize: TYPE.small, fontWeight: 600, color: NAVY, mb: 1 }}>KT Document</Typography>
        {kt.kt_doc_name ? (
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <CheckCircle sx={{ fontSize: 16, color: "#16a34a" }} />
            <Typography title={kt.kt_doc_name} sx={{ fontSize: TYPE.small, color: NAVY, ...ELLIPSIS }}>{kt.kt_doc_name}</Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: TYPE.caption, color: SLATE }}>No document uploaded yet</Typography>
        )}
        <Button component="label" size="small" startIcon={<Upload sx={{ fontSize: 16 }} />}
          disabled={uploading}
          sx={{ mt: 1, textTransform: "none", fontWeight: 600, color: INDIGO }}>
          {uploading ? "Uploading…" : kt.kt_doc_name ? "Replace Document" : "Upload Document"}
          <input type="file" hidden onChange={handleUploadKT} />
        </Button>
      </Box>

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={primaryBtn}>
          {saving ? "Saving…" : "Save KT Details"}
        </Button>
      </Box>
    </SectionCard>
  );
}

// ── Assets ────────────────────────────────────────────────────────────────────
function AssetsTab({ ob, empId, onRefresh }) {
  const assets = ob.assets || [];
  const [saving, setSaving] = useState(null);
  const [error, setError]   = useState("");

  const handleUpdate = async (idx, field, value) => {
    if (saving !== null) return;
    setSaving(idx); setError("");
    try {
      await api(`${API_BASE}/offboarding/${empId}/assets/${idx}`, {
        method: "PUT", headers: JSON_HEADERS, body: JSON.stringify({ [field]: value }),
      });
      onRefresh("Asset updated");
    } catch (e) {
      setError(e?.message || "Failed to update asset");
    } finally {
      setSaving(null);
    }
  };

  const pendingCount  = assets.filter(a => a.return_status === "Pending").length;
  const returnedCount = assets.filter(a => a.return_status === "Returned").length;

  return (
    <SectionCard title="Asset Recovery"
      icon={<Security sx={{ fontSize: 18, color: INDIGO }} />}
      action={
        <Chip label={`${returnedCount}/${assets.length} returned`} size="small"
          sx={{ fontWeight: 700,
            bgcolor: pendingCount === 0 ? "#dcfce7" : "#fef9c3",
            color: pendingCount === 0 ? "#166534" : "#854d0e" }} />
      }>
      <ErrorAlert error={error} onClose={() => setError("")} />
      {assets.length === 0 && (
        <Typography sx={{ fontSize: TYPE.small, color: SLATE, textAlign: "center", py: 3 }}>No assets recorded for this employee.</Typography>
      )}
      <Box display="flex" flexDirection="column" gap={1.5}>
        {assets.map((asset, idx) => (
          <Box key={`asset-${idx}`} sx={{
            display: "flex", alignItems: "center", gap: 2,
            p: 1.5, borderRadius: `${RADIUS.sm}px`,
            border: `1px solid ${asset.return_status === "Returned" ? "#bbf7d0" : LINE}`,
            bgcolor: asset.return_status === "Returned" ? "#f0fdf4" : COLORS.surfaceTint,
          }}>
            <Box sx={{ flexShrink: 0 }}>
              {asset.return_status === "Returned"
                ? <CheckCircle sx={{ fontSize: 18, color: "#16a34a" }} />
                : <HourglassEmpty sx={{ fontSize: 18, color: "#cbd5e1" }} />}
            </Box>
            <Box flex={1} minWidth={0}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{asset.asset_name}</Typography>
              {asset.asset_id && (
                <Typography sx={{ fontSize: 11, color: SLATE }}>ID: {asset.asset_id}</Typography>
              )}
            </Box>
            <FormControl size="small" sx={{ minWidth: 136 }} disabled={saving !== null}>
              <Select value={["Pending", "Returned", "Damaged", "Missing"].includes(asset.return_status) ? asset.return_status : "Pending"}
                onChange={e => handleUpdate(idx, "return_status", e.target.value)}
                sx={{ fontSize: TYPE.small }}>
                {["Pending", "Returned", "Damaged", "Missing"].map(s => (
                  <MenuItem key={s} value={s} sx={{ fontSize: TYPE.small }}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {saving === idx && <CircularProgress size={16} sx={{ color: INDIGO }} />}
            {asset.return_date && (
              <Typography sx={{ fontSize: TYPE.caption, color: SLATE, minWidth: 88, whiteSpace: "nowrap" }}>
                {fmtDate(asset.return_date)}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
}

// ── Access Revocation ─────────────────────────────────────────────────────────
function AccessTab({ ob, empId, onRefresh }) {
  const accessList = ob.access_revocation || [];
  const [saving, setSaving]       = useState(null);
  const [revokedBy, setRevokedBy] = useState("");
  const [error, setError]         = useState("");

  const handleRevoke = async (idx) => {
    if (saving !== null) return;
    setSaving(idx); setError("");
    try {
      await api(`${API_BASE}/offboarding/${empId}/access/${idx}`, {
        method: "PUT", headers: JSON_HEADERS,
        body: JSON.stringify({ status: "Revoked", revoked_by: revokedBy }),
      });
      onRefresh("Access marked as revoked");
    } catch (e) {
      setError(e?.message || "Failed to revoke access");
    } finally {
      setSaving(null);
    }
  };

  const revokedCount = accessList.filter(a => a.status === "Revoked").length;

  return (
    <SectionCard title="Access Revocation"
      icon={<Security sx={{ fontSize: 18, color: "#dc2626" }} />}
      action={
        <Chip label={`${revokedCount}/${accessList.length} revoked`} size="small"
          sx={{ fontWeight: 700,
            bgcolor: revokedCount === accessList.length ? "#dcfce7" : "#fee2e2",
            color: revokedCount === accessList.length ? "#166534" : "#991b1b" }} />
      }>
      <ErrorAlert error={error} onClose={() => setError("")} />
      <TextField size="small" label="Revoked By (your name)" fullWidth sx={{ mb: 2 }}
        value={revokedBy} onChange={e => setRevokedBy(e.target.value)} />
      <Box display="flex" flexDirection="column" gap={1}>
        {accessList.map((acc, idx) => (
          <Box key={`access-${idx}`} sx={{
            display: "flex", alignItems: "center", gap: 2,
            p: 1.5, borderRadius: `${RADIUS.sm}px`,
            border: `1px solid ${acc.status === "Revoked" ? "#bbf7d0" : LINE}`,
            bgcolor: acc.status === "Revoked" ? "#f0fdf4" : COLORS.surfaceTint,
          }}>
            {acc.status === "Revoked"
              ? <CheckCircle sx={{ fontSize: 16, color: "#16a34a", flexShrink: 0 }} />
              : <HourglassEmpty sx={{ fontSize: 16, color: "#cbd5e1", flexShrink: 0 }} />}
            <Box flex={1}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{acc.access_name}</Typography>
              {acc.revoked_by && (
                <Typography sx={{ fontSize: 11, color: SLATE }}>
                  Revoked by {acc.revoked_by}
                  {acc.revoked_date && ` · ${fmtDate(acc.revoked_date)}`}
                </Typography>
              )}
            </Box>
            <StatusChip label={acc.status} />
            {acc.status === "Pending" && (
              <Button size="small" variant="outlined"
                onClick={() => handleRevoke(idx)} disabled={saving !== null}
                sx={{ ...rejectBtn, height: CONTROL_HEIGHT.sm, flexShrink: 0 }}>
                {saving === idx ? "Saving…" : "Mark Revoked"}
              </Button>
            )}
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
}

// ── Finance ───────────────────────────────────────────────────────────────────
function FinanceTab({ ob, empId, onRefresh }) {
  const fi = ob.finance || {};
  const { busy: saving, error, setError, run } = useAction(onRefresh);
  const [form, setForm]     = useState({
    final_salary:           fi.final_salary || 0,
    leave_encashment:       fi.leave_encashment || 0,
    bonus:                  fi.bonus || 0,
    recovery_amount:        fi.recovery_amount || 0,
    notice_period_recovery: fi.notice_period_recovery || 0,
    other_deductions:       fi.other_deductions || 0,
    remarks:                fi.remarks || "",
    approved_by:            fi.approved_by || "",
  });

  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const totalPayable    = n(form.final_salary) + n(form.leave_encashment) + n(form.bonus);
  const totalDeductions = n(form.recovery_amount) + n(form.notice_period_recovery) + n(form.other_deductions);
  const finalSettlement = totalPayable - totalDeductions;

  const handleSave = (status) => run(() => api(`${API_BASE}/offboarding/${empId}/finance`, {
    method: "PUT", headers: JSON_HEADERS,
    body: JSON.stringify({ ...form, status: status || fi.status }),
  }), status === "Approved" ? "Finance clearance approved" : "Finance details saved");

  const numField = (label, key) => (
    <Grid item xs={12} sm={6} key={key}>
      <TextField fullWidth size="small" type="number" label={label}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
    </Grid>
  );

  return (
    <SectionCard title="Finance Clearance"
      icon={<AccountBalance sx={{ fontSize: 18, color: INDIGO }} />}
      action={<StatusChip label={fi.status || "Pending"} />}>
      <ErrorAlert error={error} onClose={() => setError("")} />
      <Typography sx={{ fontSize: TYPE.overline, fontWeight: 700, color: SLATE, mb: 1.5,
        textTransform: "uppercase", letterSpacing: "0.08em" }}>
        Payable Components
      </Typography>
      <Grid container spacing={2}>
        {numField("Final Salary (₹)", "final_salary")}
        {numField("Leave Encashment (₹)", "leave_encashment")}
        {numField("Bonus (₹)", "bonus")}
      </Grid>

      <Typography sx={{ fontSize: TYPE.overline, fontWeight: 700, color: SLATE, mt: 3, mb: 1.5,
        textTransform: "uppercase", letterSpacing: "0.08em" }}>
        Deductions
      </Typography>
      <Grid container spacing={2}>
        {numField("Recovery Amount (₹)", "recovery_amount")}
        {numField("Notice Period Recovery (₹)", "notice_period_recovery")}
        {numField("Other Deductions (₹)", "other_deductions")}
      </Grid>

      <Box sx={{ mt: 3, p: 2, bgcolor: COLORS.surfaceTint, borderRadius: `${RADIUS.sm}px`, border: `1px solid ${LINE}`, fontVariantNumeric: "tabular-nums" }}>
        <Box display="flex" justifyContent="space-between" py={0.5}>
          <Typography sx={{ fontSize: TYPE.small, color: SLATE }}>Total Payable</Typography>
          <Typography sx={{ fontSize: TYPE.small, fontWeight: 700, color: "#166534", fontVariantNumeric: "tabular-nums" }}>₹{totalPayable.toLocaleString("en-IN")}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" py={0.5}>
          <Typography sx={{ fontSize: TYPE.small, color: SLATE }}>Total Deductions</Typography>
          <Typography sx={{ fontSize: TYPE.small, fontWeight: 700, color: "#dc2626", fontVariantNumeric: "tabular-nums" }}>₹{totalDeductions.toLocaleString("en-IN")}</Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box display="flex" justifyContent="space-between" py={0.5}>
          <Typography sx={{ fontSize: TYPE.body, fontWeight: 700, color: NAVY }}>Final Settlement</Typography>
          <Typography sx={{ fontSize: TYPE.cardTitle, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: finalSettlement >= 0 ? "#166534" : "#dc2626" }}>
            ₹{finalSettlement.toLocaleString("en-IN")}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Approved By"
            value={form.approved_by}
            onChange={e => setForm(f => ({ ...f, approved_by: e.target.value }))} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" multiline rows={2} label="Remarks"
            value={form.remarks}
            onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
        </Grid>
      </Grid>

      <Box display="flex" gap={1.5} mt={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={() => handleSave(fi.status)} disabled={saving}
          sx={{ ...btnBase, color: SLATE }}>
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button variant="contained" onClick={() => handleSave("Approved")} disabled={saving || fi.status === "Approved"}
          sx={approveBtn}>
          {fi.status === "Approved" ? "Finance Approved" : "Approve Finance"}
        </Button>
      </Box>
    </SectionCard>
  );
}

// ── Documents ─────────────────────────────────────────────────────────────────
function DocumentsTab({ ob, empId, onRefresh }) {
  const docs = ob.documents || [];
  const [uploading, setUploading]   = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [error, setError]           = useState("");

  const handleUpload = async (idx, file) => {
    if (!file || uploading !== null) return;
    setUploading(idx); setError("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api(`${API_BASE}/offboarding/${empId}/documents/${idx}/upload`, {
        method: "POST", headers: getAuthHeaders(), body: fd,
      });
      onRefresh("Document uploaded");
    } catch (e) {
      setError(e?.message || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const uploadedCount = docs.filter(d => d.has_file).length;

  return (
    <>
      <SectionCard title="Exit Documents"
        icon={<Description sx={{ fontSize: 18, color: INDIGO }} />}
        action={
          <Chip label={`${uploadedCount}/${docs.length} uploaded`} size="small"
            sx={{ fontWeight: 700,
              bgcolor: uploadedCount === docs.length ? "#dcfce7" : "#f1f5f9",
              color: uploadedCount === docs.length ? "#166534" : SLATE }} />
        }>
        <ErrorAlert error={error} onClose={() => setError("")} />
        {docs.length === 0 && (
          <Typography sx={{ fontSize: TYPE.small, color: SLATE, textAlign: "center", py: 3 }}>No exit documents configured.</Typography>
        )}
        <Box display="flex" flexDirection="column" gap={1}>
          {docs.map((doc, idx) => (
            <Box key={`doc-${idx}`} sx={{
              display: "flex", alignItems: "center", gap: 1.5,
              p: 1.5, borderRadius: `${RADIUS.sm}px`,
              border: `1px solid ${doc.has_file ? "#bbf7d0" : LINE}`,
              bgcolor: doc.has_file ? "#f0fdf4" : COLORS.surfaceTint,
            }}>
              {doc.has_file
                ? <CheckCircle sx={{ fontSize: 18, color: "#16a34a", flexShrink: 0 }} />
                : <HourglassEmpty sx={{ fontSize: 18, color: "#cbd5e1", flexShrink: 0 }} />}
              <Box flex={1} minWidth={0}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{doc.name}</Typography>
                <Typography title={doc.file_name || undefined} sx={{ fontSize: TYPE.caption, color: SLATE, ...ELLIPSIS }}>
                  {doc.category}{doc.file_name && ` · ${doc.file_name}`}
                </Typography>
              </Box>
              {doc.has_file && (
                <Tooltip title="View">
                  <IconButton size="small" sx={{ color: INDIGO }}
                    onClick={() => setPreviewDoc({
                      idx,
                      name: doc.name,
                      file_name: doc.file_name,
                      url: `${API_BASE}/offboarding/${empId}/documents/${idx}/file`,
                    })}>
                    <Visibility sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Button component="label" size="small"
                startIcon={<Upload sx={{ fontSize: 14 }} />}
                disabled={uploading !== null}
                sx={{ textTransform: "none", fontWeight: 600, color: INDIGO, flexShrink: 0 }}>
                {uploading === idx ? "Uploading…" : doc.has_file ? "Replace" : "Upload"}
                <input type="file" hidden
                  onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; handleUpload(idx, f); }} />
              </Button>
            </Box>
          ))}
        </Box>
      </SectionCard>

      {previewDoc && (
        <DocPreviewDialog
          open={!!previewDoc}
          onClose={() => setPreviewDoc(null)}
          url={previewDoc.url}
          name={previewDoc.name}
          fileName={previewDoc.file_name}
        />
      )}
    </>
  );
}

// ── Exit Interview ────────────────────────────────────────────────────────────
function ExitInterviewTab({ ob, empId, onRefresh }) {
  const ei = ob.exit_interview || {};
  const { busy: saving, error, setError, run } = useAction(onRefresh);
  const [form, setForm]     = useState({
    reason_for_leaving: ei.reason_for_leaving  || "",
    manager_feedback:   ei.manager_feedback    || "",
    work_env_feedback:  ei.work_env_feedback   || "",
    suggestions:        ei.suggestions         || "",
    would_recommend:    ei.would_recommend     ?? null,
    completed:          false,
  });

  const handleSave = (markComplete = false) => run(() => api(`${API_BASE}/offboarding/${empId}/exit-interview`, {
    method: "PUT", headers: JSON_HEADERS,
    body: JSON.stringify({ ...form, completed: markComplete }),
  }), markComplete ? "Exit interview completed" : "Exit interview draft saved");

  const textField = (label, key, rows = 2) => (
    <Grid item xs={12} key={key}>
      <TextField fullWidth size="small" multiline rows={rows} label={label}
        value={form[key]} disabled={ei.completed}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
    </Grid>
  );

  return (
    <SectionCard title="Exit Interview"
      icon={<RecordVoiceOver sx={{ fontSize: 18, color: INDIGO }} />}
      action={<StatusChip label={ei.completed ? "Completed" : "Pending"} />}>
      <ErrorAlert error={error} onClose={() => setError("")} />
      {ei.completed && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: `${RADIUS.sm}px` }}>
          Exit interview completed{ei.completed_at ? ` on ${fmtDate(ei.completed_at)}` : ""}
        </Alert>
      )}
      <Grid container spacing={2}>
        {textField("Reason for Leaving", "reason_for_leaving", 3)}
        {textField("Manager Feedback", "manager_feedback")}
        {textField("Work Environment Feedback", "work_env_feedback")}
        {textField("Suggestions for Improvement", "suggestions")}
        <Grid item xs={12}>
          <Typography sx={{ fontSize: TYPE.small, color: SLATE, mb: 1 }}>
            Would you recommend this company to others?
          </Typography>
          <Box display="flex" gap={1}>
            {[true, false].map(val => (
              <Button key={String(val)} size="small"
                variant={form.would_recommend === val ? "contained" : "outlined"}
                disabled={ei.completed}
                onClick={() => setForm(f => ({ ...f, would_recommend: val }))}
                sx={{
                  textTransform: "none", fontWeight: 600, borderRadius: `${RADIUS.sm}px`,
                  bgcolor: form.would_recommend === val ? (val ? "#16a34a" : "#dc2626") : "transparent",
                  borderColor: val ? "#16a34a" : "#dc2626",
                  color: form.would_recommend === val ? "#fff" : (val ? "#16a34a" : "#dc2626"),
                  "&:hover": {
                    bgcolor: form.would_recommend === val
                      ? (val ? "#15803d" : "#b91c1c")
                      : (val ? "#f0fdf4" : "#fee2e2"),
                  },
                }}>
                {val ? "Yes" : "No"}
              </Button>
            ))}
          </Box>
        </Grid>
      </Grid>
      {!ei.completed && (
        <Box display="flex" gap={1.5} mt={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => handleSave(false)} disabled={saving}
            sx={{ ...btnBase, color: SLATE }}>
            Save Draft
          </Button>
          <Button variant="contained" onClick={() => handleSave(true)} disabled={saving}
            sx={primaryBtn}>
            {saving ? "Saving…" : "Mark Complete"}
          </Button>
        </Box>
      )}
    </SectionCard>
  );
}

// ── HR Approval ───────────────────────────────────────────────────────────────
function HRApprovalTab({ ob, empId, onRefresh }) {
  const hr = ob.hr_approval || {};
  const { busy: saving, error, setError, run } = useAction(onRefresh);
  const [form, setForm]     = useState({
    approved_by: hr.approved_by || "",
    remarks:     hr.remarks     || "",
  });

  const handleAction = (action) => run(() => api(`${API_BASE}/offboarding/${empId}/hr-approval`, {
    method: "PUT", headers: JSON_HEADERS, body: JSON.stringify({ action, ...form }),
  }), action === "approve" ? "HR approval recorded" : action === "reject" ? "HR rejection recorded" : "Offboarding put on hold");

  const alreadyActed = hr.status === "Approved" || hr.status === "Rejected";

  return (
    <SectionCard title="HR Approval" icon={<VerifiedUser sx={{ fontSize: 18, color: INDIGO }} />}>
      <ErrorAlert error={error} onClose={() => setError("")} />
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography sx={{ fontSize: TYPE.small, color: SLATE }}>Current status</Typography>
        <StatusChip label={hr.status || "Pending"} />
      </Box>
      {hr.approved_at && <InfoRow label="Actioned at" value={fmtDateTime(hr.approved_at)} />}
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="HR Name / Approved By"
            value={form.approved_by} disabled={alreadyActed}
            onChange={e => setForm(f => ({ ...f, approved_by: e.target.value }))} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" multiline rows={2} label="HR Comments"
            value={form.remarks} disabled={alreadyActed}
            onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
        </Grid>
      </Grid>
      {!alreadyActed && (
        <Box display="flex" gap={1.5} mt={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => handleAction("hold")} disabled={saving}
            sx={{ ...btnBase, color: "#92400e", borderColor: "#92400e", "&:hover": { bgcolor: "#fef9c3", borderColor: "#92400e" } }}>
            Hold
          </Button>
          <Button variant="outlined" onClick={() => handleAction("reject")} disabled={saving}
            sx={rejectBtn}>
            Reject
          </Button>
          <Button variant="contained" onClick={() => handleAction("approve")} disabled={saving}
            sx={approveBtn}>
            {saving ? "Saving…" : "Approve"}
          </Button>
        </Box>
      )}
    </SectionCard>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  INITIATE DIALOG  — uses emp_id (human-readable), not MongoDB _id
// ══════════════════════════════════════════════════════════════════════════════
function InitiateDialog({ open, onClose, employee, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const [form, setForm]     = useState({
    exit_type:          "Resignation",
    resignation_date:   "",
    last_working_day:   "",
    notice_period_days: 30,
    reason:             "",
    remarks:            "",
  });

  const handleSubmit = async () => {
    // employee.emp_id is the route param the backend now expects
    const empId = employee?.emp_id;
    if (!empId) { setError("This employee has no employee ID; cannot initiate offboarding."); return; }
    if (saving) return;
    setSaving(true); setError("");
    try {
      await api(`${API_BASE}/offboarding/initiate/${empId}`, {
        method: "POST", headers: JSON_HEADERS, body: JSON.stringify(form),
      });
      // Pass back the emp_id so OffboardingDetail can fetch by it
      onSuccess(empId);
    } catch (e) {
      setError(e?.message || "Failed to initiate offboarding");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px` } }}>
      <DialogTitle sx={{ bgcolor: COLORS.surfaceTint, borderBottom: `1px solid ${LINE}`, py: 2 }}>
        <Typography component="span" sx={{ display: "block", fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, color: NAVY }}>
          Initiate Offboarding — {employee?.name || "Employee"}
        </Typography>
        <Typography component="span" sx={{ display: "block", fontSize: TYPE.caption, color: SLATE, mt: 0.5 }}>
          {[employee?.emp_id, employee?.designation].filter(Boolean).join(" · ")}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: "24px !important" }}>
        <ErrorAlert error={error} onClose={() => setError("")} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Exit Type</InputLabel>
              <Select value={form.exit_type} label="Exit Type"
                onChange={e => setForm(f => ({ ...f, exit_type: e.target.value }))}>
                {["Resignation", "Termination", "Contract End", "Retirement"].map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="date"
              label="Resignation Date" InputLabelProps={{ shrink: true }}
              value={form.resignation_date}
              onChange={e => setForm(f => ({ ...f, resignation_date: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="date"
              label="Last Working Day" InputLabelProps={{ shrink: true }}
              value={form.last_working_day}
              onChange={e => setForm(f => ({ ...f, last_working_day: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="number"
              label="Notice Period (days)"
              value={form.notice_period_days}
              onChange={e => setForm(f => ({ ...f, notice_period_days: e.target.value }))} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline rows={2}
              label="Reason for Exit" value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${LINE}` }}>
        <Button onClick={onClose} disabled={saving} sx={{ ...btnBase, color: SLATE }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}
          sx={primaryBtn}>
          {saving ? "Initiating…" : "Initiate Offboarding"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
//  DETAIL VIEW  — empId is the human-readable ID (e.g. "ZTL-301")
//  Response shape: { success, data: { emp_id, name, ..., offboarding: {...} } }
// ══════════════════════════════════════════════════════════════════════════════
function OffboardingDetail({ empId, onBack, notify }) {
  const [ob, setOb]           = useState(null);   // the embedded offboarding sub-doc
  const [employee, setEmp]    = useState(null);   // top-level employee fields
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [tab, setTab]         = useState(0);
  const [version, setVersion] = useState(0);      // remounts tab forms after a reload
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState("");

  const TABS = [
    { label: "Overview" },
    { label: "Manager Approval" },
    { label: "KT" },
    { label: "Assets" },
    { label: "Access" },
    { label: "Finance" },
    { label: "Documents" },
    { label: "Exit Interview" },
    { label: "HR Approval" },
  ];

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await api(`${API_BASE}/offboarding/${encodeURIComponent(empId)}`, { headers: getAuthHeaders() });
      // data.data contains emp_id, name, email, designation, department, ... offboarding: {...}
      const { offboarding, ...empFields } = data?.data || {};
      if (!offboarding) throw new Error("No offboarding record found for this employee");
      setOb(offboarding);
      setEmp(empFields);
      setVersion(v => v + 1);
    } catch (e) {
      setError(e?.message || "Failed to load offboarding details");
    } finally {
      setLoading(false);
    }
  }, [empId]);

  useEffect(() => { load(); }, [load]);

  // Tabs call this after a successful save: toast + reload the record.
  const handleSaved = useCallback((msg) => {
    if (msg) notify?.(msg);
    load();
  }, [notify, load]);

  const handleComplete = async () => {
    if (completing) return;
    if (!window.confirm(`Mark offboarding complete for ${employee?.name || empId}?`)) return;
    setCompleting(true); setCompleteError("");
    try {
      await api(`${API_BASE}/offboarding/${encodeURIComponent(empId)}/complete`, {
        method: "POST", headers: getAuthHeaders(),
      });
      handleSaved("Offboarding marked complete");
    } catch (e) {
      setCompleteError(e?.message || "Failed to complete offboarding");
    } finally {
      setCompleting(false);
    }
  };

  const backButton = (
    <Button startIcon={<ArrowBack />} onClick={onBack}
      sx={{ ...btnBase, mb: 2, color: SLATE }}>
      Back to Offboarding List
    </Button>
  );

  if (loading && !ob) return (
    <Box>
      {backButton}
      <Box sx={{ ...emptyCardSx, py: 10 }}>
        <CircularProgress size={32} sx={{ color: INDIGO }} />
        <Typography sx={{ fontSize: TYPE.small, color: SLATE, mt: 1.5 }}>Loading offboarding details…</Typography>
      </Box>
    </Box>
  );

  if (!ob) return (
    <Box>
      {backButton}
      <Alert severity="error" sx={{ borderRadius: `${RADIUS.sm}px` }}
        action={<Button color="inherit" size="small" onClick={load} sx={{ textTransform: "none", fontWeight: 600 }}>Retry</Button>}>
        {error || "Offboarding record not found"}
      </Alert>
    </Box>
  );

  const PIPELINE_STEPS = [
    "Initiated", "Manager Approval Pending", "HR Approval Pending",
    "Asset Collection Pending", "Finance Clearance Pending", "Completed",
  ];
  const currentIdx = PIPELINE_STEPS.indexOf(ob.status);

  return (
    <Box>
      {backButton}

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: `${RADIUS.sm}px` }}
          action={<Button color="inherit" size="small" onClick={load} sx={{ textTransform: "none", fontWeight: 600 }}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {/* Header card — uses employee fields, not the embedded ob */}
      <Card elevation={0} sx={{ border: `1px solid ${LINE}`, borderRadius: `${RADIUS.lg}px`, boxShadow: SHADOW.card,
        mb: 2, overflow: "hidden" }}>
        <Box sx={{ background: "linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%)", px: 3, py: 3 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: TYPE.overline, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>
            Offboarding
          </Typography>
          <Typography sx={{ fontFamily: FONT_DISPLAY, color: "#fff", fontWeight: 700, fontSize: TYPE.page, lineHeight: 1.25 }}>
            {employee?.name || empId}
          </Typography>
          <Box display="flex" alignItems="center" gap={1.5} mt={1} flexWrap="wrap">
            <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: TYPE.body }}>
              {[employee?.emp_id, employee?.designation, ob.exit_type].filter(Boolean).join(" · ")}
            </Typography>
            <StatusChip label={ob.status} />
            {loading && <CircularProgress size={16} sx={{ color: "#fff" }} />}
          </Box>
        </Box>
      </Card>

      {/* Pipeline progress */}
      <Box sx={{ mb: 2, p: 2, border: `1px solid ${LINE}`, borderRadius: `${RADIUS.md}px`, bgcolor: COLORS.surface, boxShadow: SHADOW.card }}>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {PIPELINE_STEPS.map((step, i) => {
            const done    = i < currentIdx || ob.status === "Completed";
            const active  = i === currentIdx && ob.status !== "Completed";
            return (
              <Box key={step} display="flex" alignItems="center" gap={1}>
                <Box sx={{
                  px: 1.5, py: 0.5, borderRadius: `${RADIUS.pill}px`, fontSize: TYPE.caption, fontWeight: 600, whiteSpace: "nowrap",
                  bgcolor: done ? "#dcfce7" : active ? COLORS.accentSoft : "#f1f5f9",
                  color:   done ? "#166534" : active ? COLORS.primaryDark : "#94a3b8",
                  border:  active ? `1.5px solid ${COLORS.primary}` : "1.5px solid transparent",
                }}>
                  {done ? "✓ " : ""}
                  {step.replace(" Pending", "")}
                </Box>
                {i < PIPELINE_STEPS.length - 1 && (
                  <Box sx={{ width: 12, height: 2, bgcolor: done ? "#bbf7d0" : LINE }} />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: `1px solid ${LINE}`, mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          variant="scrollable" scrollButtons="auto">
          {TABS.map((t, i) => (
            <Tab key={`tab-${i}`} label={t.label} />
          ))}
        </Tabs>
      </Box>

      {/* All tabs receive empId so API calls use the correct route. Keyed by
          version so their forms re-read `ob` after every reload. */}
      <Box key={version}>
        {tab === 0 && <OverviewTab       ob={ob} employee={employee} empId={empId} onRefresh={handleSaved} />}
        {tab === 1 && <ManagerApprovalTab ob={ob} empId={empId} onRefresh={handleSaved} />}
        {tab === 2 && <KTTab             ob={ob} empId={empId} onRefresh={handleSaved} />}
        {tab === 3 && <AssetsTab         ob={ob} empId={empId} onRefresh={handleSaved} />}
        {tab === 4 && <AccessTab         ob={ob} empId={empId} onRefresh={handleSaved} />}
        {tab === 5 && <FinanceTab        ob={ob} empId={empId} onRefresh={handleSaved} />}
        {tab === 6 && <DocumentsTab      ob={ob} empId={empId} onRefresh={handleSaved} />}
        {tab === 7 && <ExitInterviewTab  ob={ob} empId={empId} onRefresh={handleSaved} />}
        {tab === 8 && <HRApprovalTab     ob={ob} empId={empId} onRefresh={handleSaved} />}
      </Box>

      {ob.status === "Finance Clearance Pending" && (
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1} mt={2}>
          {completeError && <Alert severity="error" onClose={() => setCompleteError("")} sx={{ borderRadius: `${RADIUS.sm}px` }}>{completeError}</Alert>}
          <Button variant="contained" startIcon={<Done />}
            onClick={handleComplete} disabled={completing}
            sx={{ ...approveBtn, px: 3 }}>
            {completing ? "Completing…" : "Mark Offboarding Complete"}
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  DASHBOARD  — lists resigned/exiting employees
// ══════════════════════════════════════════════════════════════════════════════
const emptyCardSx = {
  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
  textAlign: "center", py: 8, border: `1px solid ${LINE}`, borderRadius: `${RADIUS.lg}px`,
  bgcolor: COLORS.surface, boxShadow: SHADOW.card,
};

function KpiCard({ label, value, color, loading }) {
  return (
    <Box sx={{ flex: "1 1 160px", p: 2, borderRadius: `${RADIUS.md}px`, border: `1px solid ${LINE}`,
      bgcolor: COLORS.surface, boxShadow: SHADOW.card, textAlign: "center" }}>
      {loading
        ? <CircularProgress size={20} sx={{ color, my: 0.5 }} />
        : <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.page, fontWeight: 800, lineHeight: 1.1, color, fontVariantNumeric: "tabular-nums" }}>{value ?? 0}</Typography>}
      <Typography sx={{ fontSize: TYPE.caption, color: SLATE, mt: 0.5 }}>{label}</Typography>
    </Box>
  );
}

function OffboardingDashboard({ onSelect }) {
  const [employees, setEmployees] = useState([]);
  const [kpis, setKpis]           = useState({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [search, setSearch]       = useState("");
  const [initiateFor, setInitiateFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    const [list, dash] = await Promise.allSettled([
      api(`${API_BASE}/offboarding/resigned-employees`, { headers: getAuthHeaders() }),
      api(`${API_BASE}/offboarding/dashboard`, { headers: getAuthHeaders() }),
    ]);
    if (list.status === "fulfilled") setEmployees(Array.isArray(list.value?.data) ? list.value.data : []);
    else setError(list.reason?.message || "Failed to load exiting employees");
    if (dash.status === "fulfilled") setKpis(dash.value?.kpis || {});
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = employees.filter(e => {
    const q = search.trim().toLowerCase();
    return (
      !q ||
      e.name?.toLowerCase().includes(q)        ||
      e.email?.toLowerCase().includes(q)       ||
      e.designation?.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q)  ||
      String(e.emp_id ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap" mb={3}>
        <Box>
          <Typography sx={{
            fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.page, lineHeight: 1.25, letterSpacing: "-0.02em",
            background: "linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
          }}>
            Offboarding
          </Typography>
          <Typography sx={{ fontSize: TYPE.body, color: SLATE, mt: 0.5 }}>
            Manage employee exits — resignations, asset recovery, finance clearance and more.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={load} disabled={loading} size="small" aria-label="Refresh"
              sx={{ width: CONTROL_HEIGHT.md, height: CONTROL_HEIGHT.md, border: `1px solid ${LINE}`, borderRadius: `${RADIUS.sm}px`, bgcolor: COLORS.surface, color: SLATE }}>
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* KPI row */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <KpiCard label="Pending Exits"      value={kpis.pending_exits}    color="#1e40af" loading={loading} />
        <KpiCard label="Completed"          value={kpis.completed_exits}  color="#166534" loading={loading} />
        <KpiCard label="Asset Recovery Due" value={kpis.asset_pending}    color="#9a3412" loading={loading} />
        <KpiCard label="Finance Pending"    value={kpis.finance_pending}  color="#9d174d" loading={loading} />
        <KpiCard label="This Month"         value={kpis.this_month_exits} color="#5b21b6" loading={loading} />
      </Box>

      <TextField fullWidth size="small" placeholder="Search by name, email, role, department…"
        value={search} onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2, "& .MuiOutlinedInput-root": { height: CONTROL_HEIGHT.lg, bgcolor: COLORS.surface, borderRadius: `${RADIUS.sm}px` } }}
        InputProps={{ startAdornment: (
          <InputAdornment position="start">
            <Search sx={{ fontSize: 18, color: SLATE }} />
          </InputAdornment>
        )}} />

      {loading ? (
        <Box sx={emptyCardSx}>
          <CircularProgress size={32} sx={{ color: INDIGO }} />
          <Typography sx={{ fontSize: TYPE.small, color: SLATE, mt: 1.5 }}>Loading exiting employees…</Typography>
        </Box>
      ) : error ? (
        <Box sx={emptyCardSx}>
          <Typography sx={{ fontSize: TYPE.body, fontWeight: 600, color: COLORS.error, mb: 1.5 }}>{error}</Typography>
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={load} sx={{ ...btnBase, height: CONTROL_HEIGHT.sm }}>
            Retry
          </Button>
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={emptyCardSx}>
          <Typography sx={{ color: SLATE, fontSize: TYPE.body }}>
            {employees.length === 0
              ? "No employees with exit status found."
              : "No employees match your search."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: `${RADIUS.lg}px`, bgcolor: COLORS.surface, boxShadow: SHADOW.card, overflow: "hidden" }}>
          {filtered.map((emp, i) => (
            <Box key={emp.emp_id || emp._id || i} sx={{
              display: "flex", alignItems: "center", gap: 2,
              px: 3, py: 2, borderTop: i === 0 ? "none" : `1px solid ${LINE}`,
              "&:hover": { bgcolor: COLORS.surfaceTint },
            }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "50%",
                bgcolor: COLORS.accentSoft, color: COLORS.primary, fontWeight: 700, fontSize: TYPE.cardTitle,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {(emp.name || "?").charAt(0).toUpperCase()}
              </Box>

              <Box flex={1} minWidth={0}>
                <Typography title={emp.name} sx={{ fontWeight: 700, fontSize: TYPE.body, color: NAVY, ...ELLIPSIS }}>{emp.name || "—"}</Typography>
                <Typography sx={{ fontSize: TYPE.caption, color: SLATE, ...ELLIPSIS }}>
                  {[emp.emp_id, emp.designation, emp.department].filter(Boolean).join(" · ")}
                </Typography>
              </Box>

              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <StatusChip label={emp.status} />
              </Box>

              {/* offboarding_status comes from the new API */}
              <StatusChip label={emp.offboarding_status} />

              {/* has_offboarding replaces the old offboarding_id check */}
              {emp.has_offboarding ? (
                <Button size="small" variant="contained"
                  onClick={() => onSelect(emp.emp_id)} disabled={!emp.emp_id}
                  sx={{ ...primaryBtn, height: CONTROL_HEIGHT.sm, flexShrink: 0 }}>
                  View
                </Button>
              ) : (
                <Button size="small" variant="outlined"
                  onClick={() => setInitiateFor(emp)}
                  sx={{ ...btnBase, height: CONTROL_HEIGHT.sm, flexShrink: 0 }}>
                  Initiate
                </Button>
              )}
            </Box>
          ))}
        </Box>
      )}

      {initiateFor && (
        <InitiateDialog
          open={!!initiateFor}
          employee={initiateFor}
          onClose={() => setInitiateFor(null)}
          onSuccess={(empId) => {
            setInitiateFor(null);
            onSelect(empId);   // empId is the human-readable ID, e.g. "ZTL-301"
          }}
        />
      )}
    </Box>
  );
}

// ── ResignedStrip — also used in HRDashboard ──────────────────────────────────
export function ResignedStrip() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    let cancelled = false;
    api(`${API_BASE}/offboarding/resigned-employees`, { headers: getAuthHeaders() })
      .then(data => { if (!cancelled) setEmployees(Array.isArray(data?.data) ? data.data : []); })
      .catch(() => { /* optional strip: hide on error */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || employees.length === 0) return null;

  const OB_STYLE = {
    "Not Initiated":             { bg: "#f1f5f9", color: "#64748b" },
    "Initiated":                 { bg: "#dbeafe", color: "#1e40af" },
    "Manager Approval Pending":  { bg: "#fef9c3", color: "#854d0e" },
    "HR Approval Pending":       { bg: "#ede9fe", color: "#5b21b6" },
    "Asset Collection Pending":  { bg: "#ffedd5", color: "#9a3412" },
    "Finance Clearance Pending": { bg: "#fce7f3", color: "#9d174d" },
    "Completed":                 { bg: "#dcfce7", color: "#166534" },
    "Cancelled":                 { bg: "#f1f5f9", color: "#64748b" },
  };
  const EMP_STYLE = {
    "Resigned":   { bg: "#fee2e2", color: "#991b1b" },
    "Terminated": { bg: "#fef3c7", color: "#92400e" },
    "On Notice":  { bg: "#fef9c3", color: "#854d0e" },
  };
  const pill = (c) => ({ fontWeight: 700, height: 22, borderRadius: `${RADIUS.pill}px`, bgcolor: c.bg, color: c.color, whiteSpace: "nowrap" });

  return (
    <Box sx={{ bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: `${RADIUS.lg}px`,
      overflow: "hidden", boxShadow: SHADOW.card, mb: 3 }}>
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${COLORS.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, color: COLORS.primaryDeep }}>
            Resigned / Exiting Employees
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.muted, mt: 0.5 }}>
            {employees.length} employee{employees.length !== 1 ? "s" : ""} pending offboarding
          </Typography>
        </Box>
        <Chip label={`${employees.length} exiting`} size="small" sx={pill({ bg: "#fee2e2", color: "#991b1b" })} />
      </Box>

      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        {employees.map((emp, i) => {
          const obStyle  = OB_STYLE[emp.offboarding_status]  || OB_STYLE["Not Initiated"];
          const empStyle = EMP_STYLE[emp.status] || EMP_STYLE["Resigned"];
          return (
            <Box key={emp.emp_id || emp._id || i} sx={{
              display: "flex", alignItems: "center", gap: 2,
              p: 1.5, borderRadius: `${RADIUS.sm}px`,
              border: `1px solid ${COLORS.border}`, bgcolor: COLORS.surfaceTint,
            }}>
              <Box sx={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                background: "linear-gradient(135deg, #7f1d1d, #dc2626)",
                color: "#fff", fontWeight: 700, fontSize: TYPE.small,
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                {(emp.name || "?")[0].toUpperCase()}
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography title={emp.name} sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 700, color: COLORS.primaryDeep, ...ELLIPSIS }}>
                  {emp.name || "—"}
                </Typography>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.muted, ...ELLIPSIS }}>
                  {[emp.emp_id, emp.designation].filter(Boolean).join(" · ")}
                </Typography>
              </Box>
              {emp.status && (
                <Chip label={humanizeStatus(emp.status)} size="small"
                  sx={{ ...pill(empStyle), display: { xs: "none", sm: "flex" } }} />
              )}
              <Chip label={humanizeStatus(emp.offboarding_status || "Not Initiated")} size="small" sx={pill(obStyle)} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN EXPORT
// ══════════════════════════════════════════════════════════════════════════════
export default function OffboardingPage({ onSaveSuccess }) {
  // Own canvas + page padding only on the standalone /OffboardingPage route.
  const { pathname } = useLocation();
  const standalone = pathname.toLowerCase().startsWith("/offboardingpage");

  // selectedEmpId is the human-readable emp_id, e.g. "ZTL-301"
  const [selectedEmpId, setSelected] = useState(null);
  const [snack, setSnack] = useState("");
  // Inside the HR dashboard its snackbar is used; standalone, our own.
  const notify = onSaveSuccess || setSnack;

  const body = selectedEmpId ? (
    <OffboardingDetail
      empId={selectedEmpId}
      onBack={() => setSelected(null)}
      notify={notify}
    />
  ) : (
    <OffboardingDashboard onSelect={setSelected} />
  );

  return (
    <ThemeProvider theme={recruiterTheme}>
      <Box sx={standalone ? { bgcolor: COLORS.background, minHeight: "100vh", p: `${PAGE_PADDING}px`, fontFamily: FONT_BODY } : { fontFamily: FONT_BODY }}>
        {body}
      </Box>
      {!onSaveSuccess && (
        <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack("")}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert onClose={() => setSnack("")} severity="success" variant="filled"
            sx={{ borderRadius: `${RADIUS.sm}px`, fontWeight: 600, bgcolor: COLORS.primary }}>
            {snack}
          </Alert>
        </Snackbar>
      )}
    </ThemeProvider>
  );
}
